import { useLoggedInMember, useLoggedInOrganization } from "@/modules/auth/states";
import { getZaniaTeamMemberRoles } from "@/modules/team/states";
import { FEATURE_ACCESS_CONFIG, FeatureName } from "@/shared/config/feature-access";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useMemo } from "react";

/**
 * Custom hook for checking feature access based on feature flags and permissions
 *
 * Returns an object with boolean values for each requested feature's access status.
 *
 * @example
 * // Check single feature
 * const { hasTpraDashboardAccess } = useFeatureAccess({ features: ['tpraDashboard'] });
 *
 * @example
 * // Check multiple features
 * const { hasTpraDashboardAccess, hasVendorsAccess } = useFeatureAccess({
 *   features: ['tpraDashboard', 'vendors']
 * });
 *
 * @param options - Configuration options
 * @param options.features - Array of feature names to check access for
 * @returns Object with camelCased access properties (has{FeatureName}Access)
 */
export const useFeatureAccess = <T extends FeatureName[]>(options: {
  features: T;
}) => {
  const flags = useFlags();
  const loggedInMember = useLoggedInMember();
  const loggedInOrganization = useLoggedInOrganization();

  const userPermissions = useMemo(
    () => (loggedInMember ? getZaniaTeamMemberRoles("", loggedInMember) : {}),
    [loggedInMember],
  );

  const checkFeatureAccess = (featureName: FeatureName): boolean => {
    const config = FEATURE_ACCESS_CONFIG[featureName];

    // If no config exists, allow access by default
    if (!config) return true;

    // Check feature flag if specified
    if (config.featureFlag) {
      const flagValue = flags[config.featureFlag];

      // Handle org-specific feature flags (object format: { "orgId": true })
      if (typeof flagValue === "object" && flagValue !== null) {
        const orgId = loggedInOrganization?.organization_id;
        if (!orgId) return false;

        // Check if current org has access
        const hasOrgAccess = flagValue[orgId] === true;
        if (!hasOrgAccess) return false;
      }
      // Handle boolean feature flags
      else if (typeof flagValue === "boolean") {
        if (!flagValue) return false;
      }
      // If flag exists but is not boolean or object, deny access
      else if (flagValue !== undefined && flagValue !== true) {
        return false;
      }
    }

    // Check permission if specified
    if (config.permission) {
      const hasPermission = userPermissions[config.permission as keyof typeof userPermissions];
      if (!hasPermission) return false;
    }

    return true;
  };

  // Build the return object dynamically based on requested features
  const accessMap = useMemo(() => {
    const result: Record<string, boolean> = {};

    for (const feature of options.features) {
      const config = FEATURE_ACCESS_CONFIG[feature];
      if (!config) {
        const propertyName = `has${feature.charAt(0).toUpperCase()}${feature.slice(1)}Access`;
        result[propertyName] = true;
        continue;
      }

      let hasAccess = true;

      // Check feature flag if specified
      if (config.featureFlag) {
        const flagValue = flags[config.featureFlag];

        if (typeof flagValue === "object" && flagValue !== null) {
          const orgId = loggedInOrganization?.organization_id;
          if (!orgId || flagValue[orgId] !== true) {
            hasAccess = false;
          }
        } else if (typeof flagValue === "boolean") {
          if (!flagValue) {
            hasAccess = false;
          }
        } else if (flagValue !== undefined && flagValue !== true) {
          hasAccess = false;
        }
      }

      // Check permission if specified
      if (hasAccess && config.permission) {
        const hasPermission = userPermissions[config.permission as keyof typeof userPermissions];
        if (!hasPermission) {
          hasAccess = false;
        }
      }

      // Convert feature name to camelCase property (e.g., 'tpraDashboard' -> 'hasTpraDashboardAccess')
      const propertyName = `has${feature.charAt(0).toUpperCase()}${feature.slice(1)}Access`;
      result[propertyName] = hasAccess;
    }

    return result;
  }, [options.features, flags, userPermissions, loggedInOrganization]);

  return accessMap as {
    [K in T[number] as `has${Capitalize<K>}Access`]: boolean;
  };
};

/**
 * Utility function to check feature access outside of React components
 * Use this in utility functions or non-React contexts
 *
 * @param featureName - Name of the feature to check
 * @param flags - Feature flags object from LaunchDarkly
 * @param userPermissions - User permissions object
 * @param organizationId - Current organization ID
 * @returns boolean indicating if user has access to the feature
 */
export const checkFeatureAccess = (
  featureName: FeatureName,
  flags: Record<string, any>,
  userPermissions: Record<string, any>,
  organizationId?: string,
): boolean => {
  const config = FEATURE_ACCESS_CONFIG[featureName];
  if (!config) return true;

  // Check feature flag
  if (config.featureFlag) {
    const flagValue = flags[config.featureFlag];

    if (typeof flagValue === "object" && flagValue !== null) {
      if (!organizationId) return false;
      const hasOrgAccess = flagValue[organizationId] === true;
      if (!hasOrgAccess) return false;
    } else if (typeof flagValue === "boolean") {
      if (!flagValue) return false;
    } else if (flagValue !== undefined && flagValue !== true) {
      return false;
    }
  }

  // Check permission
  if (config.permission) {
    const hasPermission = userPermissions[config.permission];
    if (!hasPermission) return false;
  }

  return true;
};
